import type { AppHydraItem } from "@verbleif/lib/src/composables/Api/Type/AppStore/app.interface";
import type { AppSubscriptionSyncImportHydraItem } from "@verbleif/lib/src/composables/Api/Type/AppStore/app_subscription.interface";
import type { ClientHydraItem } from "@verbleif/lib/src/composables/Api/Type/Client/client.interface";
import type { ClientSettingsHydraItem } from "@verbleif/lib/src/composables/Api/Type/ClientSettings/client_settings.interface";
import type { LabelHydraItem } from "@verbleif/lib/src/composables/Api/Type/Label/label.interface";
import type { LocationHydraItem } from "@verbleif/lib/src/composables/Api/Type/Location/location.interface";
import type { ProductHydraItem } from "@verbleif/lib/src/composables/Api/Type/Product/product.interface";
import type { PropertyHydraItem } from "@verbleif/lib/src/composables/Api/Type/Property/property.interface";
import type { PropertyGroupHydraItem } from "@verbleif/lib/src/composables/Api/Type/PropertyGroup/property_group.interface";
import type { PropertyRouteHydraCollectionItem } from "@verbleif/lib/src/composables/Api/Type/PropertyRoute/propertyRoute.interface";
import type { ReportTopicHydraItem } from "@verbleif/lib/src/composables/Api/Type/ReportTopic/report_topic.interface";
import type { SortHydraItem } from "@verbleif/lib/src/composables/Api/Type/Sort/sort.interface";
import type { SpaceHydraItem } from "@verbleif/lib/src/composables/Api/Type/Space/space.interface";
import type { UserHydraItem } from "@verbleif/lib/src/composables/Api/Type/User/user.interface";
import type { UserGroupHydraItem } from "@verbleif/lib/src/composables/Api/Type/UserGroup/user_group.interface";
import type { WarehouseHydraItem } from "@verbleif/lib/src/composables/Api/Type/Warehouse/warehouse.interface";
import type { ResourceScope } from "./resourceKeys";
import { getQueryClient } from "@verbleif/lib/src/composables/Query/queryClient";
import { iriToId } from "@verbleif/lib/src/composables/Utils/iri.helpers";
import { createGlobalState } from "@vueuse/core";
import { computed, ref, watch } from "vue";
import { api } from "../Api/api";
import { getAll } from "../Api/api.helpers";
import { useCanReadInstallsOfClient } from "../AppStore/readsInstallsOfClient";
import { useAuthStore } from "../AuthStore/useAuthStore";
import { useClientSettingsStore } from "../ClientSettingsStore/useClientSettingsStore";
import { useLocationStore } from "../LocationStore/useLocationStore";
import { useSharedStore } from "../SharedStore/useSharedStore";
import { useSystemStore } from "../SystemStore/useSystemStore";
import { useWarehouses } from "../Warehouses/useWarehouses";
import { bindQueryToRef } from "./bindQueryToRef";
import { LOGIN_SCOPE, resourceKeys, toIriRecord } from "./resourceKeys";

function scopeParams(scope: ResourceScope): URLSearchParams {
  const params = new URLSearchParams();
  if (scope.location) {
    params.set("location", scope.location);
  }
  if (scope.client) {
    params.set("client", scope.client);
  }
  return params;
}

function withActive(scope: ResourceScope): URLSearchParams {
  const params = scopeParams(scope);
  params.set("active", "true");
  return params;
}

function catalogSyncConcurrency(): number {
  const value = useAuthStore().userPreferences.value?.syncConcurrency;
  if (value === 1 || value === 2 || value === 5 || value === 10) {
    return value;
  }
  return 1;
}

function fetchCatalog<T>(options: {
  url: string
  initialParams?: URLSearchParams
  perPage?: number
  signal?: AbortSignal
  _retry?: boolean
  onPage?: (items: T[]) => void
}): Promise<T[]> {
  return getAll<T>({
    ...options,
    concurrency: catalogSyncConcurrency(),
  });
}

interface CatalogQuery<T> {
  queryKey: readonly unknown[]
  queryFn: (ctx: { signal?: AbortSignal }) => Promise<T>
}

/**
 * Catalog queries of one scope. The resource catalog binds them to the selected scope; an entity
 * dialog of another location reads the same cache entries for that location (`useEntityCatalog`).
 */
export const scopedCatalogQueries = {
  sorts: (scope: ResourceScope): CatalogQuery<SortHydraItem[]> => ({
    queryKey: resourceKeys.sorts.all(scope),
    queryFn: ({ signal }) => fetchCatalog<SortHydraItem>({ url: "/sorts", initialParams: withActive(scope), signal }),
  }),
  properties: (scope: ResourceScope, onPage?: (items: PropertyHydraItem[]) => void): CatalogQuery<PropertyHydraItem[]> => ({
    queryKey: resourceKeys.properties.all(scope),
    queryFn: ({ signal }) => fetchCatalog<PropertyHydraItem>({
      url: "/properties",
      initialParams: withActive(scope),
      signal,
      onPage,
    }),
  }),
  propertyGroups: (scope: ResourceScope, onPage?: (items: PropertyGroupHydraItem[]) => void): CatalogQuery<PropertyGroupHydraItem[]> => ({
    queryKey: resourceKeys.propertyGroups.all(scope),
    queryFn: ({ signal }) => fetchCatalog<PropertyGroupHydraItem>({
      url: "/property_groups",
      initialParams: withActive(scope),
      signal,
      onPage,
    }),
  }),
  /** `clientIri` is the client of the scope's location: its users are every user of that client. */
  users: (scope: ResourceScope, clientIri: string | null): CatalogQuery<Record<string, UserHydraItem>> => ({
    queryKey: resourceKeys.users.all(scope),
    queryFn: async ({ signal }) => {
      const items = clientIri
        ? await fetchCatalog<UserHydraItem>({ url: `/clients/${iriToId(clientIri)}/users`, signal })
        : await fetchCatalog<UserHydraItem>({ url: "/users", initialParams: scopeParams(scope), signal });
      return toIriRecord(items);
    },
  }),
  userGroups: (scope: ResourceScope): CatalogQuery<Record<string, UserGroupHydraItem>> => ({
    queryKey: resourceKeys.userGroups.all(scope),
    queryFn: async ({ signal }) => toIriRecord(await fetchCatalog<UserGroupHydraItem>({
      url: "/user_groups",
      initialParams: withActive(scope),
      signal,
    })),
  }),
  /** Warehouses belong to one location, so the scope's key holds the warehouses of `location`. */
  warehouses: (scope: ResourceScope, location: string | null): CatalogQuery<WarehouseHydraItem[]> => ({
    queryKey: resourceKeys.warehouses.all(scope),
    queryFn: ({ signal }) => {
      const params = new URLSearchParams();
      if (location) {
        params.set("location", location);
      }
      params.set("order[isDefault]", "DESC");
      params.set("order[name]", "ASC");
      return fetchCatalog<WarehouseHydraItem>({ url: "/api/warehouses", initialParams: params, signal });
    },
  }),
};

export const loginCatalog = {
  clients: {
    queryKey: resourceKeys.clients.all(LOGIN_SCOPE),
    queryFn: async ({ signal, retry = true }: { signal?: AbortSignal, retry?: boolean }) =>
      toIriRecord(await fetchCatalog<ClientHydraItem>({ url: "/clients", signal, _retry: retry })),
  },
  locations: {
    queryKey: resourceKeys.locations.all(LOGIN_SCOPE),
    queryFn: async ({ signal, retry = true }: { signal?: AbortSignal, retry?: boolean }) =>
      toIriRecord(await fetchCatalog<LocationHydraItem>({ url: "/locations", signal, _retry: retry })),
  },
  spaces: {
    queryKey: resourceKeys.spaces.all(LOGIN_SCOPE),
    queryFn: async ({ signal, retry = true }: { signal?: AbortSignal, retry?: boolean }) => {
      const items = await fetchCatalog<SpaceHydraItem>({ url: "/spaces", signal, _retry: retry });
      return items.filter(space => !!space.location);
    },
  },
};

/**
 * Login flow: fills the same cache entries the catalog observers read, so a
 * concurrent observer fetch is deduplicated instead of hitting the API twice.
 */
export async function ensureLoginCatalog(options: { retry?: boolean } = {}): Promise<void> {
  const client = getQueryClient();
  const staleTime = 5 * 60_000;
  function fetch<T>(definition: {
    queryKey: readonly unknown[]
    queryFn: (ctx: { signal?: AbortSignal, retry?: boolean }) => Promise<T>
  }) {
    return client.fetchQuery<T>({
      queryKey: definition.queryKey,
      staleTime,
      queryFn: ({ signal }) => definition.queryFn({ signal, retry: options.retry }),
    });
  }

  await fetch(loginCatalog.clients);
  await fetch(loginCatalog.locations);
  await fetch(loginCatalog.spaces);
}

/**
 * Every client or location the user can see, as a list. Reads the login catalog
 * entry (an IRI record) instead of `ensureResourceAll` on the same key, which
 * expects an array and would get the record back.
 */
export async function ensureLoginCatalogList<T>(name: "clients" | "locations"): Promise<T[]> {
  const definition: {
    queryKey: readonly unknown[]
    queryFn: (ctx: { signal?: AbortSignal }) => Promise<Record<string, unknown>>
  } = loginCatalog[name];
  const record = await getQueryClient().fetchQuery({
    queryKey: definition.queryKey,
    staleTime: 5 * 60_000,
    queryFn: ({ signal }) => definition.queryFn({ signal }),
  });
  return Object.values(record) as T[];
}

export interface UseResourceCatalogOptions {
  include?: "full" | "labels"
  getActiveTimeEntry?: () => Promise<void>
  route?: {
    meta: Record<PropertyKey, unknown>
    name?: string | symbol | null
  }
}

export const useResourceCatalog = createGlobalState((options?: UseResourceCatalogOptions) => {
  const include = options?.include ?? "full";
  const { isAuthenticated } = useAuthStore();
  const locationStore = useLocationStore();
  const {
    modeSelectedLocation,
    modeSelectedClient,
    selectedLocationObject,
    selectedLocation,
    selectedClient,
    clients,
    locations,
    spaces,
  } = locationStore;
  const systemStore = useSystemStore();
  const sharedStore = useSharedStore();
  const { warehouses } = useWarehouses();
  const clientSettingsStore = useClientSettingsStore();

  const scope = computed<ResourceScope>(() => ({
    location: modeSelectedLocation.value,
    client: modeSelectedClient.value,
  }));

  const skipRouteCheck = computed(() => {
    if (!options?.route) {
      return false;
    }
    return options.route.meta.public === true || options.route.meta.skipAuthChecks === true || options.route.name === undefined;
  });

  const loginEnabled = computed(() => isAuthenticated.value && !skipRouteCheck.value);
  const scopedEnabled = computed(() =>
    isAuthenticated.value
    && !skipRouteCheck.value
    && (scope.value.location != null || scope.value.client != null),
  );
  // The running install syncs need the install read on the organization (the API's AppSubscriptionImportBatchVoter
  // answers 403 otherwise), so they are only asked for someone who holds it there. It follows a switch of location or
  // organization and a new identity (impersonation, account switch) at once.
  const canReadInstalls = useCanReadInstallsOfClient(() => selectedLocationObject.value?.client ?? modeSelectedClient.value);
  const importBatchesEnabled = computed(() => scopedEnabled.value && canReadInstalls.value);
  const warehouseEnabled = computed(() => loginEnabled.value && selectedLocation.value != null);
  const clientSettingsEnabled = computed(() => loginEnabled.value && selectedClient.value != null);
  const propertiesFetching = ref(false);
  const propertyGroupsFetching = ref(false);

  if (include === "full") {
    bindQueryToRef(clients, () => ({
      ...loginCatalog.clients,
      enabled: loginEnabled.value,
    }), {});

    bindQueryToRef(locations, () => ({
      ...loginCatalog.locations,
      enabled: loginEnabled.value,
    }), {}, undefined, locationStore.locationsLoaded);

    bindQueryToRef(spaces, () => ({
      ...loginCatalog.spaces,
      enabled: loginEnabled.value,
    }), [], undefined, locationStore.spacesLoaded);
    watch([spaces, locations, locationStore.isCatalogLoaded], () => {
      locationStore.updateSpaceState();
    });

    bindQueryToRef(systemStore.sorts, () => ({
      ...scopedCatalogQueries.sorts(scope.value),
      enabled: scopedEnabled.value,
    }), []);

    bindQueryToRef(systemStore.properties, () => {
      const queryKey = resourceKeys.properties.all(scope.value);
      return {
        ...scopedCatalogQueries.properties(scope.value, items => getQueryClient().setQueryData(queryKey, items)),
        enabled: scopedEnabled.value,
      };
    }, [], propertiesFetching);

    bindQueryToRef(systemStore.propertyGroups, () => {
      const queryKey = resourceKeys.propertyGroups.all(scope.value);
      return {
        ...scopedCatalogQueries.propertyGroups(scope.value, items => getQueryClient().setQueryData(queryKey, items)),
        enabled: scopedEnabled.value,
      };
    }, [], propertyGroupsFetching);

    bindQueryToRef(systemStore.propertyRoutes, () => ({
      queryKey: resourceKeys.propertyRoutes.all(scope.value),
      enabled: scopedEnabled.value,
      queryFn: ({ signal }) => fetchCatalog<PropertyRouteHydraCollectionItem>({ url: "/property_routes", initialParams: withActive(scope.value), signal }),
    }), []);

    bindQueryToRef(systemStore.products, () => ({
      queryKey: resourceKeys.products.all(scope.value),
      enabled: scopedEnabled.value,
      queryFn: ({ signal }) => fetchCatalog<ProductHydraItem>({ url: "/products", initialParams: scopeParams(scope.value), signal }),
    }), []);

    bindQueryToRef(systemStore.reportTopics, () => ({
      queryKey: resourceKeys.reportTopics.all(scope.value),
      enabled: scopedEnabled.value,
      queryFn: ({ signal }) => fetchCatalog<ReportTopicHydraItem>({ url: "/report_topics", initialParams: scopeParams(scope.value), signal }),
    }), []);

    bindQueryToRef(systemStore.appSubscriptionSyncImportBatches, () => ({
      queryKey: resourceKeys.appSubscriptionImportBatches.all(scope.value),
      enabled: importBatchesEnabled.value,
      queryFn: ({ signal }) => {
        const params = scopeParams(scope.value);
        params.set("exists[failedAt]", "false");
        params.set("exists[finishedAt]", "false");
        return fetchCatalog<AppSubscriptionSyncImportHydraItem>({
          url: "/app_subscription_import_batches",
          initialParams: params,
          signal,
        });
      },
    }), []);

    bindQueryToRef(systemStore.activeApps, () => ({
      queryKey: resourceKeys.apps.all(LOGIN_SCOPE),
      enabled: loginEnabled.value,
      queryFn: ({ signal }) => fetchCatalog<AppHydraItem>({ url: "/apps", initialParams: new URLSearchParams(), signal }),
    }), []);

    bindQueryToRef(sharedStore.users, () => ({
      ...scopedCatalogQueries.users(scope.value, selectedLocationObject.value?.client ?? modeSelectedClient.value),
      enabled: scopedEnabled.value,
    }), {});

    bindQueryToRef(sharedStore.systemUsers, () => ({
      queryKey: resourceKeys.systemUsers.all(scope.value),
      enabled: scopedEnabled.value,
      queryFn: async ({ signal }) => toIriRecord(await fetchCatalog<UserHydraItem>({
        url: "/system_users",
        initialParams: scopeParams(scope.value),
        signal,
      })),
    }), {});

    bindQueryToRef(sharedStore.userGroups, () => ({
      ...scopedCatalogQueries.userGroups(scope.value),
      enabled: scopedEnabled.value,
    }), {});

    bindQueryToRef(warehouses, () => ({
      ...scopedCatalogQueries.warehouses(scope.value, selectedLocation.value),
      enabled: warehouseEnabled.value,
    }), []);

    bindQueryToRef(clientSettingsStore.current, () => ({
      queryKey: resourceKeys.clientSettings.all(scope.value),
      enabled: clientSettingsEnabled.value,
      queryFn: async ({ signal }) => {
        const { data } = await api.get<ClientSettingsHydraItem>("/api/client_settings/me", { signal });
        return data;
      },
    }), null);

    watch(sharedStore.users, () => {
      void import("../Permissions/SharedPermissions").then(({ permissionService }) => {
        permissionService.setUsersData();
      });
    });

    if (options?.getActiveTimeEntry) {
      const getActiveTimeEntry = options.getActiveTimeEntry;
      watch(
        [isAuthenticated, () => modeSelectedLocation.value ?? modeSelectedClient.value, skipRouteCheck],
        ([authenticated, selectedEntity, skip]) => {
          if (!authenticated || !selectedEntity || skip) {
            return;
          }
          void getActiveTimeEntry();
        },
        { immediate: true },
      );
    }
  }

  bindQueryToRef(systemStore.labels, () => ({
    queryKey: include === "labels" ? resourceKeys.labels.all(LOGIN_SCOPE) : resourceKeys.labels.all(scope.value),
    enabled: include === "labels" ? loginEnabled.value : scopedEnabled.value,
    queryFn: ({ signal }) => fetchCatalog<LabelHydraItem>({
      url: "/labels",
      initialParams: include === "labels" ? withActive(LOGIN_SCOPE) : withActive(scope.value),
      signal,
    }),
  }), []);

  return {
    skipRouteCheck,
    loginEnabled,
    scopedEnabled,
    isSyncingProperties: computed(() => propertiesFetching.value),
    isSyncingPropertyGroups: computed(() => propertyGroupsFetching.value),
  };
});
